import { BaseAction } from '../../store/actions';
import { ConfigUpdateItem, JourneyConfig, PreviousJourneyConfig } from '../../shared/models/journey';

export enum JourneyActions {
  GetActiveConfig = '[Journey] Get Active Config',
  GetActiveConfigSuccess = '[Journey] Get Active Config Success',
  GetActiveConfigError = '[Journey] Get Active Config Error',

  UpdateConfig = '[Journey] Update Config',
  UpdateConfigSuccess = '[Journey] Update Config Success',
  UpdateConfigError = '[Journey] Update Config Error',

  ReplaceConfig = '[Journey] Replace Config',
  ReplaceConfigSuccess = '[Journey] Replace Config Success',
  ReplaceConfigError = '[Journey] Replace Config Error',

  UseDefaultConfig = '[Journey] Use Default Config',

  ResetConfig = '[Journey] Reset Configuration',
  ResetConfigSuccess = '[Journey] Reset Configuration Success',
  ResetConfigError = '[Journey] Reset Configuration Error',

  CreateConfig = '[Journey] Create Config',
  CreateConfigSuccess = '[Journey] Create Config Success',
  CreateConfigError = '[Journey] Create Config Error',

  UploadLogo = '[Journey] Upload Logo',
  UploadLogoSuccess = '[Journey] Upload Logo Success',
  UploadLogoError = '[Journey] Upload Logo Error',

  UploadVehicleImage = '[Journey] Upload Vehicle Image',
  UploadVehicleImageSuccess = '[Journey] Upload Vehicle Image Success',
  UploadVehicleImageError = '[Journey] Upload Vehicle Image Error',
}

export class GetActiveConfig implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.GetActiveConfig;

  // createIfMissing: create and publish the default config when the org has none.
  // Only PRO orgs own their config; other orgs (e.g. child orgs) inherit it via Journey Builder.
  // isProOrg: only configs created by the console (timestamp names) count as the org's config.
  constructor(public readonly orgID: number, public readonly createIfMissing = false, public readonly isProOrg = false) {}
}

export class GetActiveConfigSuccess implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.GetActiveConfigSuccess;

  constructor(public readonly id: string, public readonly config: JourneyConfig, public readonly previous: PreviousJourneyConfig) {}
}

export class GetActiveConfigError implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.GetActiveConfigError;

  constructor(public readonly payload: string) {}
}

export class UpdateConfig implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UpdateConfig;

  constructor(public readonly orgID: number, public readonly updates: ConfigUpdateItem[]) {}
}

export class UpdateConfigSuccess implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UpdateConfigSuccess;
}

export class UpdateConfigError implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UpdateConfigError;

  constructor(public readonly payload: string) {}
}

export class ReplaceConfig implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.ReplaceConfig;

  constructor(public readonly orgID: number, public readonly config: JourneyConfig) {}
}

export class ReplaceConfigSuccess implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.ReplaceConfigSuccess;
}

export class ReplaceConfigError implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.ReplaceConfigError;

  constructor(public readonly payload: string) {}
}


export class UseDefaultConfig implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UseDefaultConfig;

  constructor(public readonly payload: object) {}
}

export class ResetConfig implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.ResetConfig;

  constructor(public readonly orgID: number) {}
}

export class ResetConfigSuccess implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.ResetConfigSuccess;
}

export class ResetConfigError implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.ResetConfigError;

  constructor(public readonly payload: string) {}
}

export class CreateConfig implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.CreateConfig;

  constructor(public readonly orgID: number, public readonly config: JourneyConfig) {}
}

export class CreateConfigSuccess implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.CreateConfigSuccess;

  constructor(public readonly payload: string) {}
}

export class CreateConfigError implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.CreateConfigError;

  constructor(public readonly payload: string) {}
}

export class UploadLogo implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UploadLogo;

  constructor(
    public readonly orgID: number,
    public readonly logo: File,
    public readonly path: string,
    public readonly key?: string
  ) {}
}

export class UploadLogoSuccess implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UploadLogoSuccess;

  constructor(public readonly payload: string) {}
}

export class UploadLogoError implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UploadLogoError;

  constructor(public readonly payload: string) {}
}

export class UploadVehicleImage implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UploadVehicleImage;

  constructor(
    public readonly orgID: number,
    public readonly vehicleImage: File,
    public readonly logoFile: File,
    public readonly logoUrl: string,
    public readonly vehicleType: string
  ) {}
}

export class UploadVehicleImageSuccess implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UploadVehicleImageSuccess;

  constructor(public readonly payload: string) {}
}

export class UploadVehicleImageError implements BaseAction<JourneyActions> {
  readonly type = JourneyActions.UploadVehicleImageError;

  constructor(public readonly payload: string) {}
}

export type JourneyAction =
  GetActiveConfig | GetActiveConfigSuccess | GetActiveConfigError |
  UpdateConfig | UpdateConfigSuccess | UpdateConfigError |
  UseDefaultConfig |
  ResetConfig | ResetConfigSuccess | ResetConfigError |
  CreateConfig | CreateConfigSuccess | CreateConfigError |
  UploadLogo | UploadLogoSuccess | UploadLogoError |
  ReplaceConfig | ReplaceConfigSuccess | ReplaceConfigError |
  UploadVehicleImage | UploadVehicleImageSuccess | UploadVehicleImageError;
